// =============================================================================
// SimSense Platform — Ticket Attachment Renderer (OT-304)
// =============================================================================
// Parses screenshot URLs from ticket body and renders image thumbnails.

import { useState } from "react";

const SCREENSHOT_SEPARATOR = "--- Screenshots ---";
const IMAGE_URL_PATTERN = /^https?:\/\/.+\.(png|jpe?g|webp|gif)(\?.*)?$/i;

/** Split a ticket body into text content and screenshot URLs */
export function parseTicketBody(body: string): { text: string; screenshots: string[] } {
  const idx = body.indexOf(SCREENSHOT_SEPARATOR);
  if (idx === -1) return { text: body, screenshots: [] };

  const text = body.slice(0, idx).trimEnd();
  const urlSection = body.slice(idx + SCREENSHOT_SEPARATOR.length).trim();
  const screenshots = urlSection
    .split("\n")
    .map(l => l.trim())
    .filter(l => IMAGE_URL_PATTERN.test(l));

  return { text, screenshots };
}

/** Renders screenshot thumbnails with lightbox */
export function TicketAttachments({ urls }: { urls: string[] }) {
  const [lightbox, setLightbox] = useState<string | null>(null);

  if (urls.length === 0) return null;

  return (
    <>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "12px",
        paddingTop: "12px",
        borderTop: "1px solid var(--border)",
      }}>
        <div style={{
          width: "100%",
          fontSize: "10px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: "var(--text3)",
          marginBottom: "2px",
        }}>
          Attachments ({urls.length})
        </div>
        {urls.map((url, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setLightbox(url); }}
            style={{
              padding: 0,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid var(--border)",
              borderRadius: "6px",
              cursor: "pointer",
              overflow: "hidden",
              width: "80px",
              height: "80px",
              flexShrink: 0,
              transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--accent)"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
          >
            <img
              src={url}
              alt={`Attachment ${i + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Lightbox overlay */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            padding: "40px",
          }}
        >
          <img
            src={lightbox}
            alt="Screenshot"
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              borderRadius: "8px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
            }}
          />
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: "absolute",
              top: "16px",
              right: "20px",
              background: "rgba(255,255,255,0.1)",
              border: "none",
              color: "#fff",
              fontSize: "24px",
              cursor: "pointer",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            &times;
          </button>
        </div>
      )}
    </>
  );
}
